import React, { useMemo } from 'react';
import { APICONSTANTS } from 'src/components/Constants';
import { SnackContext } from 'src/store/ContextStore';
import axios from 'src/axios';
import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  Tooltip,
  Typography
} from '@mui/material';
import {
  AddCircleRounded,
  AutoAwesome,
  BookmarkRounded,
  Refresh
} from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import { AppContext } from 'src/store/ContextStore';
import { EVENTS } from 'src/components/admin/appReducer';
import { TILE_COLOURS } from './tileColours';
import { detectCrossEvidence } from '../../pedigree-tool/analysis/detectors/crossEvidence';
import { molecularSupport } from '../../pedigree-tool/analysis/molecularSupport';

function Suggestions({ data, setDataset, analysis }) {
  const { setSnack } = React.useContext(SnackContext);
  const { content, dispatchUpdate } = React.useContext(AppContext);
  const { pedigreeSelectedUsers: users } = content;
  const { selectedUser: user } = users;

  let user_id = useMemo(() => {
    return user?.user_id || user?._id;
  }, [user]);

  // Id for family member
  let familyMemberId = useMemo(() => {
    return data?.familyMemberId;
  }, [data]);

  // Id for proband
  let proBandId = useMemo(() => {
    return data?.proBandId;
  }, [data]);

  const [fetchData, setFetchData] = React.useState({
    hpoTerms: [],
    excludedHpoTerms: [],
    Genes: [],
    Diagnoses: []
  });
  // True while the per-node /genotypes /diagnoses /phenotypes fetch is in
  // flight. Used to distinguish "node has no clinical data" (show empty-state
  // CTA) from "fetch hasn't completed yet" (show spinner). Starts true so we
  // don't flash the CTA on first mount.
  const [fetchLoading, setFetchLoading] = React.useState(true);

  // Single source of truth for how each Profile Match `type` maps to the
  // patient record: which fetchData key holds the patient's current items,
  // which AppContext event to dispatch on save, which backend endpoint and
  // payload shape to use, and the user-facing label.
  // `sendFullList` reflects each endpoint's contract — Genes' /genotypes
  // overwrites the whole array; Diagnoses/Phenotypes append-and-dedupe.
  const TYPE_MAP = useMemo(
    () => ({
      Genes: {
        fetchKey: 'Genes',
        event: EVENTS.SET_PEDIGREE_GENOTYPES,
        apiConstant: 'genotypes',
        payloadKey: 'genotypes',
        label: 'Genotypes',
        sendFullList: true,
        storeAsId: false
      },
      Diagnoses: {
        fetchKey: 'Diagnoses',
        event: EVENTS.SET_PEDIGREE_DIAGNOSES,
        apiConstant: 'diagnoses',
        payloadKey: 'diagnoses',
        label: 'Diagnoses',
        sendFullList: false,
        storeAsId: false
      },
      Diseases: {
        fetchKey: 'Diagnoses', // diseases are stored under Diagnoses
        event: EVENTS.SET_PEDIGREE_DIAGNOSES,
        apiConstant: 'diagnoses',
        payloadKey: 'diagnoses',
        label: 'Diseases',
        sendFullList: false,
        storeAsId: false
      },
      Phenotypes: {
        fetchKey: 'hpoTerms', // patient phenotypes live here as bare HP ids
        event: EVENTS.SET_PEDIGREE_PHENOTYPES,
        apiConstant: 'phenotypes',
        payloadKey: 'phenotypes',
        label: 'Phenotypes',
        sendFullList: false,
        storeAsId: true
      }
    }),
    []
  );

  const addSuggestion = (type, value) => {
    if (!type || !value) return;
    const cfg = TYPE_MAP[type];
    if (!cfg) return;

    const currentValues = fetchData[cfg.fetchKey] || [];
    const exists = cfg.storeAsId
      ? currentValues.includes(value.id)
      : currentValues.some((item) => item?.id === value.id);

    if (exists) {
      setSnack({
        status: true,
        msg: 'Already added!',
        severity: 'error'
      });
      return;
    }

    // Optimistic update so the bookmark icon flips immediately and the row
    // becomes inert. The authoritative refresh comes from getHPOTerm after
    // the dispatch below.
    const optimisticItem = cfg.storeAsId
      ? value.id
      : { id: value.id, name: value.name };
    const newValues = [...currentValues, optimisticItem];
    setFetchData((prev) => ({
      ...prev,
      [cfg.fetchKey]: newValues
    }));

    const id = proBandId ?? familyMemberId;
    const payload = {
      familyMemberId: id,
      user_id: user_id,
      isProband: proBandId ? true : false,
      ...(cfg.sendFullList
        ? { [cfg.payloadKey]: newValues }
        : { [cfg.payloadKey]: [{ id: value.id, name: value.name }] })
    };

    axios
      .post(APICONSTANTS[cfg.apiConstant], payload)
      .then((res) => {
        if (!res.data?.success) return;

        // Family-wide accumulator dispatch (fires the Profile Match
        // fingerprints and the per-node refetch in this component).
        dispatchUpdate({
          type: cfg.event,
          value: [{ id: value.id, name: value.name }]
        });

        // The Diseases / Phenotypes / Genotypes sidebar menus watch
        // users.selectedUser.<list> / users.family[i].<list>, not the
        // family-wide accumulator, so the SET_PEDIGREE_* event above is
        // not enough to refresh them. Push the new item into the affected
        // member's record so the menus' existing useEffects fire.
        const isProband = !!proBandId;
        const memberData = isProband
          ? users?.selectedUser
          : (users?.family || []).find(
              (m) => m.familyMemberId === familyMemberId
            );
        if (memberData) {
          const recordListKey =
            type === 'Phenotypes'
              ? 'phenotypes'
              : type === 'Genes'
              ? 'genotypes'
              : 'diagnoses';
          const existingList = memberData[recordListKey] || [];
          if (!existingList.some((it) => it?.id === value.id)) {
            const updatedList = [
              ...existingList,
              { id: value.id, name: value.name }
            ];
            dispatchUpdate({
              type: EVENTS.UPDATE_PEDIGREE_SELECTED_USER,
              value: isProband
                ? { ...memberData, [recordListKey]: updatedList }
                : { _id: memberData._id, [recordListKey]: updatedList }
            });
          }
        }

        // Keep the pedigree dataset (diagnoses chips on the node label)
        // in sync for the Diseases/Diagnoses path.
        if (
          (type === 'Diagnoses' || type === 'Diseases') &&
          res.data.allDiagnoses
        ) {
          setDataset((prev) =>
            prev.map((elem) =>
              elem.pid === data?.data?.pid
                ? { ...elem, diagnoses: res.data.allDiagnoses }
                : elem
            )
          );
        }

        setSnack({
          status: true,
          msg: `Added to ${cfg.label}`,
          severity: 'success'
        });
      })
      .catch(() => {
        // Roll back the optimistic update on failure so the bookmark icon
        // doesn't lie about persisted state.
        setFetchData((prev) => ({
          ...prev,
          [cfg.fetchKey]: currentValues
        }));
        setSnack({
          status: true,
          msg: 'An error has occurred!',
          severity: 'error'
        });
      });
  };

  const getHPOTerm = () => {
    const id = proBandId ?? familyMemberId;
    if (!id || !user_id) {
      setFetchLoading(false);
      return;
    }

    setFetchLoading(true);
    Promise.all([
      axios.get(
        APICONSTANTS.getGenotypesById
          .replace(':user_id', user_id)
          .replace(':familyMemberId', id),
        {
          params: { isProband: proBandId ? true : false }
        }
      ),
      axios.get(
        APICONSTANTS.getDiagnosesById
          .replace(':user_id', user_id)
          .replace(':familyMemberId', id),
        {
          params: { isProband: proBandId ? true : false }
        }
      ),
      axios.get(
        APICONSTANTS.getPhenotypesById
          .replace(':user_id', user_id)
          .replace(':familyMemberId', id),
        {
          params: { isProband: proBandId ? true : false }
        }
      )
    ])
      .then(([geno_res, diagno_res, pheno_res]) => {
        const phenotypes = pheno_res?.data?.phenotypes || [];
        setFetchData({
          Genes: geno_res?.data.genotypes?.map((gene) => ({
            id: gene.id,
            name: gene.name
          })),
          Diagnoses: diagno_res.data.diagnoses.map((dig) => ({
            id: dig.id,
            name: dig.name
          })),
          hpoTerms: phenotypes
            .filter((hp) => hp.observed !== false)
            .map((hp) => hp.id),
          excludedHpoTerms: phenotypes
            .filter((hp) => hp.observed === false)
            .map((hp) => hp.id)
        });
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: 'Error fetching Values',
          severity: 'error'
        });
      })
      .finally(() => {
        setFetchLoading(false);
      });
  };

  // Fingerprint of the family-wide phenotype set in AppContext. We don't
  // use this as the hpoIds source — `pedigreeSelectedUsers.phenotypes`
  // accumulates phenotypes across the whole selected pedigree, not just the
  // selected node — but a change to it is a reliable signal that *some*
  // phenotype was added/removed, so we re-run getHPOTerm to fetch the
  // current node's per-person list.
  const phenotypeFingerprint = useMemo(() => {
    const phens = users?.phenotypes;
    if (!Array.isArray(phens)) return '';
    return phens
      .map((p) => p?.id)
      .filter(Boolean)
      .sort()
      .join(',');
  }, [users]);

  // Same change-signal pattern for diseases: the AppContext-level
  // diagnoses accumulator may be family-wide, but a change to it means
  // *something* changed in the disease list and we should re-fetch.
  const diagnosesFingerprint = useMemo(() => {
    const dx = users?.diagnoses;
    if (!Array.isArray(dx)) return '';
    return dx
      .map((d) => d?.id)
      .filter(Boolean)
      .sort()
      .join(',');
  }, [users]);

  React.useEffect(() => {
    getHPOTerm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    proBandId,
    familyMemberId,
    user_id,
    phenotypeFingerprint,
    diagnosesFingerprint
  ]);

  // diseaseIds passed to the Profile Match panel. fetchData.Diagnoses
  // already comes from the per-person /diagnoses endpoint so the IDs are
  // correctly scoped to the selected node.
  const diseaseIds = useMemo(() => {
    const dx = fetchData.Diagnoses;
    if (!Array.isArray(dx)) return [];
    return dx.map((d) => d?.id).filter((id) => typeof id === 'string');
  }, [fetchData.Diagnoses]);

  const localHasInputs = fetchData.hpoTerms.length > 0 || diseaseIds.length > 0;

  // ID sets of the patient's currently-recorded items, keyed by the same
  // `type` strings the Profile Match panel uses. Lets each row flag itself
  // as already-in-record (renders a bookmark instead of the + action).
  // Phenotypes include both present and absent — both are "already on the
  // record" from the suggestion-picker's perspective.
  const existingIds = useMemo(
    () => ({
      Diseases: new Set(
        (fetchData.Diagnoses || []).map((d) => d?.id).filter(Boolean)
      ),
      Genes: new Set((fetchData.Genes || []).map((g) => g?.id).filter(Boolean)),
      Phenotypes: new Set([
        ...(fetchData.hpoTerms || []),
        ...(fetchData.excludedHpoTerms || [])
      ])
    }),
    [
      fetchData.Diagnoses,
      fetchData.Genes,
      fetchData.hpoTerms,
      fetchData.excludedHpoTerms
    ]
  );

  // Patient's gene symbols (normalised) for gene↔disease convergence on the
  // disease cards, e.g. "BRCA1 (NCBIGene:672)" → "BRCA1".
  const patientGeneSet = useMemo(
    () =>
      new Set(
        (fetchData.Genes || [])
          .map((g) => (g?.name || '').split(' (')[0].trim().toUpperCase())
          .filter(Boolean)
      ),
    [fetchData.Genes]
  );

  // Genes the selected individual carries a PATHOGENIC variant in (from the
  // analyzer's per-individual output). Gates the Phase C molecular-support
  // re-rank so a benign/VUS held gene doesn't boost a disease.
  const patientPathogenicGenes = useMemo(
    () =>
      new Set(
        analysis?.individuals?.[String(data?.pid)]?.pathogenicGenes || []
      ),
    [analysis, data]
  );

  return (
    <Grid container spacing={1}>
      {localHasInputs ? (
        <ProfileMatchBox
          hpoIds={fetchData.hpoTerms}
          excludedHpoIds={fetchData.excludedHpoTerms}
          diseaseIds={diseaseIds}
          patientGeneSet={patientGeneSet}
          addSuggestion={addSuggestion}
          existingIds={existingIds}
          family={analysis?.family}
          patientPathogenicGenes={patientPathogenicGenes}
        />
      ) : fetchLoading ? (
        <Grid
          sx={{
            gap: 1,
            alignItems: 'center',
            display: 'flex',
            fontSize: '.8rem',
            bgcolor: 'rgba(46, 55, 71, 0.03)',
            border: '1px solid rgba(46, 55, 71, 0.08)',
            p: 1,
            borderRadius: 1
          }}
          size={12}
        >
          <CircularProgress size={14} />
          <span>Loading suggestions…</span>
        </Grid>
      ) : (
        <Grid
          sx={{
            gap: 1,
            alignItems: 'center',
            display: 'flex',
            fontSize: '.8rem',
            fontWeight: 'bold',
            bgcolor: '#fff4e6',
            p: 1,
            borderRadius: 1
          }}
          size={12}
        >
          <AutoAwesome fontSize="small" />
          <span>
            Add phenotypes and other clinical data to generate suggestions
          </span>
        </Grid>
      )}
    </Grid>
  );
}

export default Suggestions;

// Profile-level match — ranks diseases/genes against the full HPO profile
// using Phenomizer-style semantic similarity (with Jaccard as a fallback).
// Mirrors hpo.jax.org's profile-search behaviour.
const ProfileMatchBox = ({
  hpoIds,
  excludedHpoIds,
  diseaseIds,
  patientGeneSet,
  addSuggestion,
  existingIds,
  family,
  patientPathogenicGenes
}) => {
  const [results, setResults] = React.useState({
    diseases: [],
    genes: [],
    phenotypes: []
  });
  const [loading, setLoading] = React.useState(true);
  const [mode, setMode] = React.useState(null);
  // Bumped by the manual refresh button to force a re-fetch independent of
  // the hpoIds dependency. Useful after a terminology-service re-seed
  // invalidates the in-memory cache, or as a generic retry.
  const [refreshTick, setRefreshTick] = React.useState(0);

  const totalInputs = (hpoIds?.length || 0) + (diseaseIds?.length || 0);

  // Cross-evidence: a family-level syndrome pattern (from the pure pedigree
  // analyzer) that corroborates a Profile Match disease/gene for this person.
  // Distinct from Phase A's "gene + phenotype" convergence — this is the
  // "family + variant" signal. Matched here (where `results` live) rather than
  // inside analyzePedigree, which stays pure and family-scoped.
  const crossEvidence = useMemo(() => {
    const empty = { diseases: new Set(), genes: new Set() };
    if (!family) return empty;
    const edges = detectCrossEvidence(
      { family },
      { diseases: results.diseases, genes: results.genes }
    );
    const out = { diseases: new Set(), genes: new Set() };
    for (const e of edges) {
      if (e.target.resultType === 'disease')
        out.diseases.add(e.target.resultId);
      else if (e.target.resultType === 'gene')
        out.genes.add(String(e.target.resultId).toUpperCase());
    }
    return out;
  }, [family, results]);

  // Phase C — molecular support: when the patient carries a pathogenic variant
  // in a gene linked to a ranked disease, boost that disease, weighted by the
  // family's inheritance pattern for it (dominant ⇒ strong, recessive ⇒
  // carrier-level). Re-sort diseases on the boosted score; the row enriches the
  // existing convergence chip rather than adding a new one.
  const rankedDiseases = useMemo(() => {
    const inheritanceByDisease = family?.inheritanceByDisease || {};
    return (results.diseases || [])
      .map((d) => {
        const linkedPathogenic = (d.associatedGenes || []).filter((g) =>
          patientPathogenicGenes?.has?.(String(g).toUpperCase())
        );
        const support = molecularSupport({
          holdsPathogenicLinkedGene: linkedPathogenic.length > 0,
          inheritancePattern: inheritanceByDisease[d.id]
        });
        return {
          ...d,
          score: (d.score || 0) * (1 + support.boost),
          _support: support,
          _supportGenes: linkedPathogenic
        };
      })
      .sort((a, b) => (b.score || 0) - (a.score || 0));
  }, [results.diseases, patientPathogenicGenes, family]);

  React.useEffect(() => {
    if (totalInputs === 0) {
      setResults({ diseases: [], genes: [], phenotypes: [] });
      setLoading(false);
      return;
    }
    setLoading(true);
    const basePayload = {
      hpoIds,
      excludedHpoIds: excludedHpoIds || [],
      diseaseIds,
      limit: 10
    };
    Promise.all([
      axios.post('/terminology/profile-search', {
        ...basePayload,
        kind: 'disease'
      }),
      axios.post('/terminology/profile-search', {
        ...basePayload,
        kind: 'gene'
      }),
      axios.post('/terminology/profile-search', {
        ...basePayload,
        kind: 'phenotype'
      })
    ])
      .then(([d, g, p]) => {
        setResults({
          diseases: d.data?.results || [],
          genes: g.data?.results || [],
          phenotypes: p.data?.results || []
        });
        setMode(d.data?.mode || null);
      })
      .catch(() => setResults({ diseases: [], genes: [], phenotypes: [] }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hpoIds.join(','),
    (excludedHpoIds || []).join(','),
    (diseaseIds || []).join(','),
    refreshTick
  ]);

  const hpoCount = hpoIds?.length || 0;
  const excludedCount = excludedHpoIds?.length || 0;
  const dxCount = diseaseIds?.length || 0;
  const headerSummary = [
    hpoCount > 0 && `${hpoCount} phenotype${hpoCount === 1 ? '' : 's'}`,
    excludedCount > 0 && `${excludedCount} ruled out`,
    dxCount > 0 && `${dxCount} disease${dxCount === 1 ? '' : 's'}`
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Grid size={12}>
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          bgcolor: 'rgba(46, 55, 71, 0.03)',
          p: 1,
          borderRadius: 1,
          flexDirection: 'column',
          border: '1px solid rgba(46, 55, 71, 0.08)'
        }}
      >
        <Box
          sx={{
            gap: 1,
            alignItems: 'center',
            display: 'flex',
            fontSize: '.85rem',
            fontWeight: 'bold'
          }}
        >
          <AutoAwesome fontSize="small" />
          <span>Profile Match</span>
          <Box
            sx={{
              fontSize: '.7rem',
              fontWeight: 400,
              color: 'text.secondary',
              flex: 1
            }}
          >
            ranked across {headerSummary}
            {mode ? ` · ${mode}` : ''}
          </Box>
          <IconButton
            size="small"
            onClick={() => setRefreshTick((n) => n + 1)}
            disabled={loading}
            title="Refresh profile match"
            sx={{ p: 0.25 }}
          >
            <Refresh fontSize="small" />
          </IconButton>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <CircularProgress size={18} />
          </Box>
        ) : (
          <>
            <ProfileMatchList
              title="Diseases"
              items={rankedDiseases}
              type="Diseases"
              addSuggestion={addSuggestion}
              totalInputs={totalInputs}
              existingIds={existingIds?.Diseases}
              patientGeneSet={patientGeneSet}
              crossEvidenceIds={crossEvidence.diseases}
            />
            <ProfileMatchList
              title="Genes"
              items={results.genes}
              type="Genes"
              addSuggestion={addSuggestion}
              totalInputs={totalInputs}
              existingIds={existingIds?.Genes}
              crossEvidenceIds={crossEvidence.genes}
            />
            <ProfileMatchList
              title="Phenotypes to check"
              items={results.phenotypes}
              type="Phenotypes"
              addSuggestion={addSuggestion}
              totalInputs={totalInputs}
              existingIds={existingIds?.Phenotypes}
            />
          </>
        )}
      </Box>
    </Grid>
  );
};

const ProfileMatchList = ({
  title,
  items,
  type,
  addSuggestion,
  totalInputs,
  existingIds,
  patientGeneSet,
  crossEvidenceIds
}) => {
  // Pick the colour family matching how these entities are stored in the
  // patient record — Diseases → disease tile palette, Genes → genotype,
  // Phenotypes → phenotype tile palette.
  const palette =
    type === 'Diseases'
      ? TILE_COLOURS.disease
      : type === 'Phenotypes'
      ? TILE_COLOURS.phenotype
      : TILE_COLOURS.genotype;
  if (!items || items.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ px: 1, fontSize: '.8rem', fontWeight: 'bold' }}>{title}</Box>
        <Box sx={{ px: 1, fontSize: '.75rem', color: 'text.secondary' }}>
          No matches
        </Box>
      </Box>
    );
  }
  // Normalise to 0..1 across the displayed set so the bar widths are
  // comparable. The absolute score's units depend on mode, but relative
  // ranking is what the clinician cares about.
  const maxScore = Math.max(...items.map((x) => x.score || 0)) || 1;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Box sx={{ px: 1, fontSize: '.8rem', fontWeight: 'bold' }}>{title}</Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5
        }}
      >
        {items.map((res) => (
          <ProfileMatchRow
            key={res.id}
            res={res}
            type={type}
            relScore={res.score / maxScore}
            addSuggestion={addSuggestion}
            palette={palette}
            totalInputs={totalInputs}
            existing={!!existingIds && existingIds.has(res.id)}
            patientGeneSet={patientGeneSet}
            crossEvidenceIds={crossEvidenceIds}
          />
        ))}
      </Box>
    </Box>
  );
};

const ProfileMatchRow = ({
  res,
  type,
  relScore,
  addSuggestion,
  palette,
  totalInputs,
  existing,
  patientGeneSet,
  crossEvidenceIds
}) => {
  const [loading, setLoading] = React.useState(false);
  const onAdd = async () => {
    if (existing) return;
    setLoading(true);
    await addSuggestion(type, { id: res.id, name: res.name });
    setLoading(false);
  };
  const pct = Math.round((relScore || 0) * 100);
  // Candidate genes for a disease (gene↔disease associations from the
  // terminology service) + convergence: which of those the patient holds.
  const associatedGenes =
    type === 'Diseases' && Array.isArray(res.associatedGenes)
      ? res.associatedGenes
      : [];
  const heldGenes = associatedGenes.filter((g) =>
    patientGeneSet?.has?.(String(g).toUpperCase())
  );
  // Gene rows match the family-syndrome gene by symbol ("BRCA1"); disease rows
  // by id ("OMIM:114480").
  const crossKey =
    type === 'Genes'
      ? (res.name || res.id || '').split(' (')[0].trim().toUpperCase()
      : res.id;
  const hasCrossEvidence = !!crossEvidenceIds && crossEvidenceIds.has(crossKey);
  // Phase C molecular-support phrase, weighted by the family inheritance
  // pattern — enriches the convergence chip tooltip (no separate chip).
  const support = res._support;
  const supportText = support?.level
    ? support.basis === 'recessive_carrier'
      ? 'recessive inheritance — carrier-level support only'
      : support.basis === 'dominant'
      ? `${support.level} molecular support (dominant inheritance)`
      : `${support.level} molecular support`
    : null;
  return (
    <Box
      sx={{
        position: 'relative',
        bgcolor: palette.background,
        border: `1px solid ${palette.border}`,
        borderLeft: `3px solid ${palette.accent}`,
        borderRadius: 1,
        px: 1.25,
        py: 0.75,
        cursor: existing ? 'default' : 'pointer',
        overflow: 'hidden',
        '&:hover': existing ? undefined : { filter: 'brightness(0.97)' }
      }}
      onClick={onAdd}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          width: `${pct}%`,
          // Subtle score-bar overlay tinted with the palette's accent.
          bgcolor: palette.accent,
          opacity: 0.08,
          pointerEvents: 'none'
        }}
      />
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: palette.text,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {res.name}
          </Typography>
          <Typography variant="body2" sx={{ color: palette.subtext }}>
            {res.id}
            {(() => {
              // Phenotype-kind rows have no perQuery (they're frequency ×
              // IC aggregations). totalAnnotations carries the supporting
              // disease count — show "supported by N records".
              if (type === 'Phenotypes') {
                const n = res.totalAnnotations || 0;
                return ` · supported by ${n} record${n === 1 ? '' : 's'}`;
              }
              // Disease/gene rows: count patient inputs that have a
              // meaningfully similar partner in this entity (MICA-IC ≥
              // threshold). Denominator is total inputs (phenotypes +
              // diseases), so the caption stays honest when the disease
              // record also contributes to ranking.
              const SIMILARITY_THRESHOLD = 1.0;
              if (Array.isArray(res.perQuery) && res.perQuery.length > 0) {
                const related = res.perQuery.filter(
                  (p) => (p?.similarity || 0) >= SIMILARITY_THRESHOLD
                ).length;
                const denom = totalInputs || res.perQuery.length;
                return ` · ${related}/${denom} input${
                  denom === 1 ? '' : 's'
                } related`;
              }
              if (Number.isFinite(res.matchedTerms?.length)) {
                return ` · ${res.matchedTerms.length} exact match${
                  res.matchedTerms.length === 1 ? '' : 'es'
                }`;
              }
              return '';
            })()}
          </Typography>
          {hasCrossEvidence && (
            <Box sx={{ mt: 0.25 }}>
              <Tooltip title="A family-level syndrome pattern in the pedigree corroborates this result">
                <Chip
                  label="family + variant"
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '.72rem',
                    textTransform: 'capitalize',
                    bgcolor: '#7b1fa2',
                    color: '#fff',
                    '& .MuiChip-label': { px: 0.75 }
                  }}
                />
              </Tooltip>
            </Box>
          )}
          {associatedGenes.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 0.5,
                mt: 0.25
              }}
            >
              {heldGenes.length > 0 && (
                <Tooltip
                  title={`Patient carries ${heldGenes.join(
                    ', '
                  )} — molecular and phenotype evidence converge${
                    supportText ? ` · ${supportText}` : ''
                  }`}
                >
                  <Chip
                    label="gene + phenotype"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '.72rem',
                      textTransform: 'capitalize',
                      bgcolor: palette.accent,
                      color: '#fff',
                      '& .MuiChip-label': { px: 0.75 }
                    }}
                  />
                </Tooltip>
              )}
              <Typography
                variant="caption"
                sx={{ color: palette.subtext, fontSize: '.78rem' }}
              >
                Genes: {associatedGenes.slice(0, 6).join(', ')}
                {associatedGenes.length > 6 ? '…' : ''}
              </Typography>
            </Box>
          )}
        </Box>
        {loading ? (
          <CircularProgress size={14} />
        ) : existing ? (
          <BookmarkRounded
            fontSize="small"
            titleAccess="Already in patient record"
            sx={{ color: palette.accent }}
          />
        ) : (
          <AddCircleRounded fontSize="small" sx={{ color: palette.accent }} />
        )}
      </Box>
    </Box>
  );
};
